import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { app } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { formService } from '../../services/formService';
import { Upload, FileText, Sparkles, ShieldCheck, LayoutTemplate } from 'lucide-react';

const ConversionPage = ({ onSave }) => {
  const navigate = useNavigate();
  const functions = getFunctions(app);
  const { user } = useAuth();
  
  const [file, setFile] = useState(null);
  const [logo, setLogo] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [strategy, setStrategy] = useState('strict');
  const [theme, setTheme] = useState('light');
  const [layoutMode, setLayoutMode] = useState('original');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
  };

  const startConversion = async () => {
    if (!file) return;
    if (!user) {
      toast.error('Please sign in to convert a form.');
      return;
    }

    setIsProcessing(true);
    setProgress(10);

    try {
        // 1. Service handles the Storage upload and returns the URL
        // This keeps the component from needing to know about Storage refs
        const uploaded = await formService.uploadPDF(file, user.uid);
        const fileUrl = uploaded.url;
        setProgress(40);

        // 2. Call the AI Cloud Function
        const convertPdf = httpsCallable(functions, 'convertPdfToHtmlForm', { timeout: 540000 });
        
        const { data: result } = await convertPdf({
        fileUrl,
        fileName: file.name,
        instructions,
        strategy,
        layoutMode
        });
        
        setProgress(80);

        // 3. Service handles saving the final document to Firestore
        const normalizedFields = (result.fields || []).map((field, index) => ({
          id: field.id || `field-${index + 1}`,
          label: field.label || `Field ${index + 1}`,
          type: field.type || 'text',
          required: Boolean(field.required),
          placeholder: field.placeholder || ''
        }));

        const newForm = {
          title: result.formTitle || file.name.replace('.pdf', ''),
          fields: normalizedFields,
          config: { strategy, theme, brandLogo: logo, instructions },
          layoutMode: result.layoutMode || layoutMode,
          pages: result.pages || [],
          html: result.html || '',
          sourcePdf: {
            url: fileUrl,
            path: uploaded.path,
            name: uploaded.name,
            originalName: uploaded.originalName
          }
        };

        const savedForm = await formService.saveForm(newForm, user.uid);
        
        setProgress(100);
        setTimeout(() => {
        if (onSave) onSave(savedForm);
        navigate(`/form/${savedForm.id}`);
        }, 500);

    } catch (error) {
        console.error("AI Conversion Pipeline Failed:", error);
        toast.error(error?.message || 'Conversion failed. Please try again.');
        setIsProcessing(false);
    }
    };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#f8fafc_0%,#eef2ff_45%,#e0f2fe_100%)] text-slate-900 pb-24">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/80 backdrop-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={() => navigate('/forms')} className="text-sm font-medium text-slate-500 hover:text-slate-800">
            ← Back to Forms
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-500" />
            <h1 className="text-lg font-semibold text-slate-900">Form Conversion Studio</h1>
          </div>
          <div className="text-xs text-slate-400">Cloud AI</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        <section className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8">
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Source Document</h2>
                  <p className="text-sm text-slate-500">Upload a PDF to detect fields and build your form.</p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600">
                  <ShieldCheck className="h-3 w-3" />
                  Secure Upload
                </span>
              </div>

              <div
                onClick={() => fileInputRef.current?.click()}
                className={`group relative flex h-56 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-all ${
                  file ? 'border-emerald-400/70 bg-emerald-50' : 'border-indigo-200 bg-indigo-50/50 hover:border-indigo-400'
                }`}
              >
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${file ? 'bg-emerald-500 text-white' : 'bg-indigo-600 text-white'}`}>
                    <Upload className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {file ? file.name : 'Drag & drop or click to upload'}
                    </p>
                    <p className="text-xs text-slate-500">PDF only • Max 30MB recommended</p>
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />
              </div>

              {!file && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-700">No file selected yet</p>
                      <p className="text-xs text-slate-500">Select a PDF to enable conversion settings.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/40 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Design & Layout</h3>
                  <p className="text-sm text-slate-500">Pick the look and structure you want the AI to produce.</p>
                </div>
                <LayoutTemplate className="h-5 w-5 text-indigo-500" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {['original', 'reflow'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => setLayoutMode(mode)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      layoutMode === mode
                        ? 'border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-200/40'
                        : 'border-slate-200 hover:border-indigo-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-900">
                      {mode === 'original' ? 'Original Layout' : 'Reflowed Design'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {mode === 'original'
                        ? 'Overlay fields on the scanned PDF layout.'
                        : 'Generate a modern form redesign from the prompt.'}
                    </p>
                  </button>
                ))}
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">AI Instructions</label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  placeholder="Example: Create a clean, accessible form with grouped sections and clear labels."
                  rows={5}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/40 p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">AI Strategy</h3>
              <div className="grid grid-cols-2 gap-3">
                {['strict', 'creative'].map(s => (
                  <button
                    key={s}
                    onClick={() => setStrategy(s)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      strategy === s
                        ? 'border-emerald-500 bg-emerald-50 shadow-md shadow-emerald-200/40'
                        : 'border-slate-200 hover:border-emerald-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-900">{s === 'strict' ? 'Strict' : 'Creative'}</p>
                    <p className="text-xs text-slate-500">
                      {s === 'strict'
                        ? 'Stay close to the original form structure.'
                        : 'Allow the AI to reimagine form layout and grouping.'}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/40 p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">Theme</h3>
              <div className="grid grid-cols-2 gap-3">
                {['light', 'dark'].map(t => (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      theme === t
                        ? 'border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-200/40'
                        : 'border-slate-200 hover:border-indigo-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-900">{t === 'light' ? 'Light' : 'Dark'}</p>
                    <p className="text-xs text-slate-500">
                      {t === 'light' ? 'Bright, clean presentation.' : 'Rich, high-contrast styling.'}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {isProcessing && (
              <div className="rounded-3xl border border-indigo-200 bg-indigo-50 p-5">
                <div className="flex items-center justify-between text-xs font-semibold text-indigo-700">
                  <span>Processing form...</span>
                  <span>{progress}%</span>
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-indigo-100">
                  <div className="h-full bg-indigo-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-slate-500">
            {file ? 'Ready to convert your PDF into a form.' : 'Upload a PDF to continue.'}
          </div>
          <button
            onClick={startConversion}
            disabled={isProcessing || !file}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3 text-sm font-semibold transition-all ${
              isProcessing || !file
                ? 'bg-slate-200 text-slate-500'
                : 'bg-indigo-600 text-white shadow-lg shadow-indigo-300/40 hover:bg-indigo-700'
            }`}
          >
            <Sparkles className="h-4 w-4" />
            {isProcessing ? 'Converting...' : 'Generate Form'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversionPage;
